import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import mapboxgl from 'mapbox-gl';
import ReactMapGL, { PointerEvent, MapLoadEvent, Marker } from 'react-map-gl';
import styled from 'styled-components';
import pinLocationUrl from '../../static/images/workday/location.svg';
import { breakpoints } from '../../utils/styledComponents';

export interface MapAddressModel {
  countryCode: string;
  geolocation: {
    latitude?: string;
    longitude?: string;
  };
  lines: { type: string; value: string }[];
  municipality: string;
  postCode: string;
  region: string;
  subMunicipality: string;
  subRegion: string;
  uid: string;
}

export const MAP_STYLE = 'mapbox://styles/utmost/ck3fri07f0u341cpqdsf6dy3o';

export const getLocation = (label: string, locationData: LocationData): string => {
  const aContext = locationData?.id?.includes(label)
    ? locationData
    : locationData?.context?.filter((c) => c?.id.includes(label))?.[0];
  return label === 'country' ? (aContext as LocationContext)?.short_code?.toLocaleUpperCase() : (aContext?.text ?? '');
};

export const getLocationFromMapBoxFeature = (locationData: LocationData): Partial<MapAddressModel> => ({
  lines: [
    { type: 'Line 1', value: '' },
    { type: 'Line 2', value: '' },
    {
      type: 'Line 3',
      value:
        (locationData?.address ? `${locationData?.address} ` : '') + getLocation('address', locationData) ||
        locationData?.properties?.address ||
        ''
    }
  ],
  postCode: getLocation('postcode', locationData),
  municipality: getLocation('place', locationData),
  region: getLocation('region', locationData),

  countryCode: getLocation('country', locationData),
  geolocation: {
    latitude: locationData?.latitude || locationData?.center?.[1]?.toString() || '',
    longitude: locationData?.longitude || locationData?.center?.[0]?.toString() || ''
  }
});

const DEFAULT_VIEWPORT = {
  latitude: 20,
  longitude: 0,
  zoom: 1.2
};

export interface Viewport {
  latitude: number;
  longitude: number;
  zoom: number;
}

export interface LocationData extends Result {
  latitude?: string;
  longitude?: string;
}

export interface GeocoderResultEvent {
  result: Result;
}
interface GeocoderResultProperties {
  address: string;
}
interface LocationContext {
  id: string;
  short_code: string;
  text: string;
  language: string;
}

export interface MapboxGeocoderProps {
  proximity?: 'ip';
}

interface Result {
  id?: string;
  address?: string;
  place_name?: string;
  center?: number[];
  context?: LocationContext[];
  text?: string;
  properties?: GeocoderResultProperties;
  geometry?: { type: string; coordinates: number[] };
  latitude?: string;
  longitude?: string;
  'place_name_en-EN'?: string;
  place_type?: string[];
  relevance?: number;
  'text_en-EN'?: string;
  type?: string;
}

export interface LocationMapProps {
  onLocationChange?: (locationData: LocationData) => void;
  defaultViewport?: Viewport;
  initialLocation?: Partial<Viewport>;
  isReadOnly?: boolean;
  height?: string;
  width?: string;
  className?: string;
  mapboxGeocoderProps?: MapboxGeocoderProps;
  mapboxAccessToken: string | undefined;
}

const fetchNearMapBoxLocations = async (
  location: {
    longitude: string;
    latitude: string;
  },
  mapboxAccessToken: string | undefined
): Promise<GeocoderResultEvent> => {
  try {
    const apiClient = fetch;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${location.longitude}%2C${location.latitude}.json?access_token=${mapboxAccessToken}`;
    const resp = await (await apiClient(url)).json();
    if (!resp?.features[0]) throw new Error(resp);
    return { result: resp?.features[0] };
  } catch {
    return { result: {} };
  }
};

export const LocationMap: FC<React.PropsWithChildren<LocationMapProps>> = ({
  onLocationChange,
  defaultViewport,
  initialLocation,
  isReadOnly = false,
  width = '100%',
  height = '400px',
  className,
  mapboxGeocoderProps,
  mapboxAccessToken
}) => {
  const [markerLocation, setMarketLocation] = useState<{ latitude?: string; longitude?: string }>({});
  const [viewport, setViewport] = useState(defaultViewport || DEFAULT_VIEWPORT);

  useEffect(() => {
    if (initialLocation?.latitude && initialLocation?.longitude)
      setMarketLocation({
        latitude: initialLocation?.latitude?.toString(),
        longitude: initialLocation?.longitude?.toString()
      });
  }, [initialLocation]);

  const geoCoderRef = useRef<HTMLDivElement>(null);

  const flyTo = useMemo(() => ({ bearing: 0, speed: 5, curve: 1 }), []);

  const handleLocationChange = useCallback(
    ({ result }: GeocoderResultEvent, lat?: string, long?: string) => {
      const latitude = lat || result?.center?.[1]?.toString();
      const longitude = long || result?.center?.[0]?.toString();
      setMarketLocation({
        latitude,
        longitude
      });

      onLocationChange?.({
        ...result,
        latitude,
        longitude
      });
    },
    [onLocationChange]
  );

  const handleMouseClick = useCallback(
    async (e: PointerEvent) => {
      const longitude = e.lngLat[0]?.toString() ?? '0';
      const latitude = e.lngLat[1]?.toString() ?? '0';
      const geoCoderResult = await fetchNearMapBoxLocations({ longitude, latitude }, mapboxAccessToken);
      handleLocationChange(geoCoderResult, latitude, longitude);
    },
    [handleLocationChange, mapboxAccessToken]
  );

  const onLoad = useCallback(
    ({ target: map }: MapLoadEvent) => {
      const geoCoder = new MapboxGeocoder({
        mapboxgl,
        proximity: mapboxGeocoderProps?.proximity ?? false,
        trackProximity: false,
        marker: false,
        accessToken: mapboxAccessToken,
        flyTo,
        reverseGeocode: true,
        limit: 4,
        language: 'en-EN'
      });
      geoCoder.on('result', handleLocationChange);
      map.on('moveend', () =>
        setViewport({ zoom: map.getZoom(), latitude: map.getCenter().lat, longitude: map.getCenter().lng })
      );
      if (geoCoderRef?.current) {
        geoCoderRef?.current.appendChild(geoCoder.onAdd(map));
      }
    },
    [mapboxGeocoderProps?.proximity, flyTo, handleLocationChange, mapboxAccessToken]
  );

  return (
    <StyledMap
      className={`${className} chromatic-ignore`}
      data-test="location__dialog__map__container"
      $width={width}
      $height={height}
    >
      {!isReadOnly && <div id="geo-coder" ref={geoCoderRef} />}
      {/* @ts-ignore this component does not support "children" prop */}
      <ReactMapGL
        data-test="location__react__map__gl"
        {...viewport}
        bearing={undefined}
        pitch={undefined}
        mapStyle={MAP_STYLE}
        onViewportChange={(el) => setViewport(el)}
        mapboxApiAccessToken={mapboxAccessToken}
        width="100%"
        height="100%"
        onLoad={onLoad}
        onNativeClick={!isReadOnly ? handleMouseClick : undefined}
        maxZoom={19}
      >
        {(markerLocation?.latitude || markerLocation?.longitude) && (
          // @ts-ignore this component does not support "children" prop
          <Marker
            latitude={parseFloat(markerLocation?.latitude ?? '0')}
            longitude={parseFloat(markerLocation?.longitude ?? '0')}
            offsetLeft={-30}
            offsetTop={-50}
          >
            <img data-test="location__dialog__map__marker" src={pinLocationUrl} alt="map marker" />
          </Marker>
        )}
      </ReactMapGL>
    </StyledMap>
  );
};

const StyledMap = styled.div<{ $width?: string; $height?: string }>`
  width: ${({ $width }) => $width};

  @media (max-width: ${breakpoints.sm}px) {
    width: 100%;
  }

  height: ${({ $height }) => $height};
  padding: 0 0 0 0;
  position: relative;

  .mapboxgl-ctrl-geocoder {
    width: 100%;
    max-width: 100%;
  }

  #geo-coder {
    position: absolute;
    top: 0;
    left: 0;
    padding: 1rem;
    margin-bottom: 1rem;
    width: 100%;
    max-width: 100%;
  }
`;
